import React, {useCallback} from 'react';
import {Badge, Button, type SxProps} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import type {EntitySubType} from 'types';
import {EntityMainTypes} from 'types';
import {useLocalization} from 'localization';
import {ColorPalette} from 'theme';
import {Text} from 'components';
import type {SearchMainEntityType} from '../types';

type Props<TEntityType> = {
  type: TEntityType;
  isSelected: boolean;
  onSelected?: (type: TEntityType) => void;
  loading?: boolean;
  count?: number;
};

const baseSx: SxProps = {
  borderRadius: 4,
  color: ColorPalette.burgundy,
  paddingX: 1.5,
  paddingY: 1,
};

const mainEntitySx: SxProps = {
  ...baseSx,
  backgroundColor: ColorPalette.beige,
};

const subEntitySx: SxProps = {
  ...baseSx,
  backgroundColor: ColorPalette.beige50,
};

const selectedSx: SxProps = {
  borderColor: ColorPalette.burgundy,
  borderWidth: 1,
  borderStyle: 'solid',
};

const sxMap: {
  [isMain in 'main' | 'sub']: {
    [isSelected in 'selected' | 'notSelected']: SxProps;
  };
} = {
  main: {
    selected: {...mainEntitySx, ...selectedSx},
    notSelected: mainEntitySx,
  },
  sub: {
    selected: {...subEntitySx, ...selectedSx},
    notSelected: subEntitySx,
  },
};

const badgeSx: SxProps = {
  backgroundColor: ColorPalette.burgundy,
  color: ColorPalette.beige,
};

export function EntityButton<
  TEntityType extends SearchMainEntityType | EntitySubType,
>({type, isSelected, onSelected, loading, count}: Props<TEntityType>) {
  const {tLoose} = useLocalization();
  const level = EntityMainTypes.includes(type as SearchMainEntityType)
    ? 'main'
    : 'sub';
  const sx = sxMap[level][isSelected ? 'selected' : 'notSelected'];
  const title = tLoose(
    `general.entity.${type as SearchMainEntityType | EntitySubType}`,
  );
  const handleClick = useCallback(() => {
    onSelected && onSelected(type);
  }, [onSelected, type]);
  return (
    <Badge
      badgeContent={
        loading ? <CircularProgress size={10} color={'info'} /> : count
      }
      slotProps={{
        badge: {
          sx: badgeSx,
        },
      }}>
      <Button sx={sx} onClick={handleClick}>
        <Text variant="body2">{title}</Text>
      </Button>
    </Badge>
  );
}
